import React, { useMemo, useState, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Header from './components/Header';
import Filters from './components/Filters';
import Row from './components/Row';
import TableHeaderSection from './components/TableHeaderSection';
import NoteModalContent from './components/NoteModalContent';
import NoteModalButtons from './components/NoteModalButtons';
import PayoutAmtModalContent from '../../../../components/PayoutAmtModalContent';
import TableBox from '../../../../components/TableBox';
import Modal from '../../../../components/Modal';
import Button from '../../../../components/Button';
import Toast from '../../../../components/Toast';
import { toast } from 'react-toastify';
import { validateNoteForm } from './validations';
import { AuthContext } from '../../../../contexts/AuthContext';

import { HeadCellType, TableContextProvider, Validation } from '../../../../interfaces/common';
import { ListFilters, NoteForm } from '../../../../interfaces/payout.interface';
import { buildHeadCells, getStartEndOfMonth, hasResourseAccess } from '../../../../utils/methods';
import { GENERIC_LOOKUP_KEYS, VALIDATION_FIELD_MAPPING, HEAD_CELL_SORT_MAPPING } from '../../../../utils/appConstant';
import useSorting from '../../../../hooks/useSorting';
import usePagination from '../../../../hooks/usePagination';
import useUrlParams from '../../../../hooks/useUrlParams';
import useFetch from '../../../../hooks/useFetch';
import PayoutService from '../../../../services/payout.service';
import PropertyService from '../../../../services/property.service';
import PlatformService from '../../../../services/platform.service';
import UserService from '../../../../services/user.service';
import CommonService from '../../../../services/common.service';
import { SortOrder, Modules, PermissionModulesEnum, PermissionActionsEnum } from '../../../../enums';
import { usePersistentFilters } from '../../../../hooks/usePersistentFilters';

const List: React.FC = () => {
  const { t } = useTranslation();
  const { loggedInUser, permissions, isSystemAdmin } = useContext(AuthContext);

  const canVerify = hasResourseAccess(isSystemAdmin, permissions, {
    accessibleTo: [{ [PermissionModulesEnum.Reconciliation]: [PermissionActionsEnum.PayoutVerification] }],
  });

  const defaultFilters: ListFilters = {
    searchText: '',
    property: '',
    managedBy: '',
    platform: '',
    bankAccount: '',
    payoutType: '',
    payoutStatus: '',
    reservationStatus: '',
    action: '',
    payoutManagedBy: '',
    checkoutOnly: false,
    ...getStartEndOfMonth(),
  };

  const { initialFilters, saveFilters, resetFilters } = usePersistentFilters<ListFilters>('filters_reconciliation_payout', defaultFilters);

  const [filters, setFilters] = useState<ListFilters>(initialFilters);

  const [dropdownSearchData, setDropdownSearchData] = useState<any>({
    property: '',
    managedBy: '',
    platform: '',
    bankAccount: '',
    payoutType: '',
    payoutStatus: '',
    reservationStatus: '',
    action: '',
    payoutManagedBy: '',
  });

  const [kpis, setKpis] = useState<{ matched: string; unmatched: string; ratio: string }>({
    matched: '0.00',
    unmatched: '0.00',
    ratio: '0',
  });
  const [counter, setCounter] = useState<number>(0);

  // ---- Checkbox / select state ----
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);

  // ---- Modal state: payout amount modal ----
  const [payoutAmtModal, setPayoutAmtModal] = useState<{ open: boolean; item: any | null }>({
    open: false,
    item: null,
  });

  // ---- Modal state: note modal ----
  const [noteModal, setNoteModal] = useState<{ open: boolean; item: any | null }>({
    open: false,
    item: null,
  });
  const [noteFormData, setNoteFormData] = useState<NoteForm>({ action: '', notes: '', override: false });
  const [noteValidation, setNoteValidation] = useState<Validation>({ validate: true, fields: [] });
  const [noteLoading, setNoteLoading] = useState(false);

  const headCells: HeadCellType[] = buildHeadCells([
    // VALIDATION_FIELD_MAPPING.select,
    VALIDATION_FIELD_MAPPING.property,
    VALIDATION_FIELD_MAPPING.reservation,
    VALIDATION_FIELD_MAPPING.hostPayout,
    VALIDATION_FIELD_MAPPING.payoutDetails,
    VALIDATION_FIELD_MAPPING.payoutAmt,
    VALIDATION_FIELD_MAPPING.payoutMatchStatus,
    VALIDATION_FIELD_MAPPING.action,
    VALIDATION_FIELD_MAPPING.noteAction,
  ], 'payoutList');

  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const { order, orderBy, setOrder, setOrderBy } = useSorting({
    defaultOrder: (searchParams.get('order') as SortOrder) || SortOrder.asc,
    defaultOrderBy: searchParams.get('orderBy') || VALIDATION_FIELD_MAPPING.property,
  });

  const { page, perPage, setPage, setPerPage } = usePagination({
    defaultPage: Number(searchParams.get('page')) || 1,
  });

  useUrlParams({
    type: Modules.reconciliation,
    uri: Modules.reconciliation + '/payout',
    perPage,
    page,
    order,
    orderBy,
  });

  const { total, data: payouts, setData: setPayouts, loading } = useFetch({
    service: PayoutService,
    func: 'list',
    payload: {
      page,
      perPage,
      orderBy: (HEAD_CELL_SORT_MAPPING.payoutList as any)[orderBy] || orderBy,
      order,
      ...filters,
    },
    dependency: [
      order, orderBy, page, perPage,
      filters.searchText, filters.property, filters.managedBy, filters.platform,
      filters.bankAccount, filters.payoutType, filters.payoutStatus,
      filters.reservationStatus, filters.action, filters.payoutManagedBy, filters.checkoutOnly,
      filters.startDate, filters.endDate,
      counter,
    ],
    onSuccess: (response: any) => {
      setKpis({
        matched: response.kpis?.matched ?? '0.00',
        unmatched: response.kpis?.unmatched ?? '0.00',
        ratio: response.kpis?.ratio ?? '0',
      });
      setSelectedIds([]);
      setSelectAll(false);
    },
    onError: () => {
      setKpis({ matched: '0.00', unmatched: '0.00', ratio: '0' });
    },
  });

  // ---- Row 1: Property ----
  const { data: allProperties } = useFetch({
    service: PropertyService,
    func: 'lookup',
    payload: { searchText: '', is_active: '1' },
    dependency: [],
  });
  const { data: properties } = useFetch({
    service: PropertyService,
    func: 'lookup',
    payload: { searchText: dropdownSearchData.property, is_active: '1' },
    dependency: [dropdownSearchData.property],
  });

  // ---- Row 1: Managed By ----
  const { data: allManagers } = useFetch({
    service: UserService,
    func: 'managersLookup',
    payload: { searchText: '', is_active: '1' },
    dependency: [],
  });
  const { data: managers } = useFetch({
    service: UserService,
    func: 'managersLookup',
    payload: { searchText: dropdownSearchData.managedBy, is_active: '1' },
    dependency: [dropdownSearchData.managedBy],
  });

  // ---- Row 1: Platform ----
  const { data: allPlatforms } = useFetch({
    service: PlatformService,
    func: 'lookup',
    payload: { searchText: '' },
    dependency: [],
  });
  const { data: platforms } = useFetch({
    service: PlatformService,
    func: 'lookup',
    payload: { searchText: dropdownSearchData.platform },
    dependency: [dropdownSearchData.platform],
  });

  // ---- Row 2: Reservation Status ----
  const { data: allReservationStatus } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.reservationStatus, searchText: '' },
    dependency: [],
  });
  const { data: reservationStatus } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.reservationStatus, searchText: dropdownSearchData.reservationStatus },
    dependency: [dropdownSearchData.reservationStatus],
  });

  // ---- Row 2: Bank Account ----
  const { data: allBankAccounts } = useFetch({
    service: PayoutService,
    func: 'lookupBankAccounts',
    payload: { searchText: '' },
    dependency: []
  });

  const { data: bankAccounts } = useFetch({
    service: PayoutService,
    func: 'lookupBankAccounts',
    payload: { searchText: dropdownSearchData.bankAccount },
    dependency: [dropdownSearchData.bankAccount]
  });

  // ---- Row 2: Payout Type ----
  const { data: allPayoutTypes } = useFetch({
    service: PayoutService,
    func: 'lookupPayoutTypes',
    payload: { searchText: '' },
    dependency: [],
  });
  const { data: payoutTypes } = useFetch({
    service: PayoutService,
    func: 'lookupPayoutTypes',
    payload: { searchText: dropdownSearchData.payoutType },
    dependency: [dropdownSearchData.payoutType],
  });

  // ---- Row 2: Payout Status ----
  const { data: allPayoutStatuses } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.reconciliationPayoutStatus, searchText: '' },
    dependency: []
  });
  const { data: payoutStatuses } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.reconciliationPayoutStatus, searchText: dropdownSearchData.payoutStatus },
    dependency: [dropdownSearchData.payoutStatus]
  });

  // ---- Row 3: Action ----
  const { data: allActions } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.reconciliationVerificationStatus, searchText: '', orderBy: 'order_by', order: 'asc' },
    dependency: []
  });

  const { data: actions } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.reconciliationVerificationStatus, searchText: dropdownSearchData.action, orderBy: 'order_by', order: 'asc' },
    dependency: [dropdownSearchData.action]
  });

  // ---- Row 3: Payout Managed By ----
  const { data: allPayoutManagedBy } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.whoCollectsMoney, searchText: '' },
    dependency: [],
  });
  const { data: payoutManagedBy } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.whoCollectsMoney, searchText: dropdownSearchData.payoutManagedBy },
    dependency: [dropdownSearchData.payoutManagedBy],
  });

  // ---- Handlers ----
  const handleApplyFilters = useCallback((newFilters: ListFilters) => {
    setPage(1);
    setFilters(newFilters);
    saveFilters(newFilters);
  }, [saveFilters, setPage]);

  const handleResetFilters = useCallback(() => {
    setPage(1);
    setFilters(defaultFilters);
    resetFilters();
  }, [resetFilters, setPage]);

  const handleDropdownSearchInputChange = useCallback((event: any, name: string) => {
    setDropdownSearchData((prev: any) => ({ ...prev, [name]: event.target.value }));
  }, []);

  const handleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      setSelectAll(next.length === payouts.length && payouts.length > 0);
      return next;
    });
  }, [payouts]);

  const handleSelectAll = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    setSelectedIds(checked ? payouts.map((p: any) => p.id) : []);
  }, [payouts]);

  const handlePayoutAmtClick = useCallback((item: any) => {
    setPayoutAmtModal({ open: true, item });
  }, []);
  const handlePayoutAmtClose = useCallback(() => {
    setPayoutAmtModal({ open: false, item: null });
  }, []);

  const handleNoteClick = useCallback((item: any) => {
    setNoteFormData({
      action: item?.action === '' ? '__blank__' : (item?.action || ''),
      notes: item?.notes || '',
      override: false,
    });
    setNoteValidation({ validate: true, fields: [] });
    setNoteModal({ open: true, item });
  }, []);
  const handleNoteClose = useCallback(() => {
    setNoteModal({ open: false, item: null });
  }, []);

  const handleNoteInputChange = useCallback((event: any, name: string) => {
    setNoteFormData((prev) => ({
      ...prev,
      [name]: event.target.type === 'checkbox' ? event.target.checked : event.target.value,
    }));
  }, []);

  const handleNoteInputBlur = useCallback((event: any, name: string) => {
    let newValidation = validateNoteForm({
      ...noteFormData,
      [name]: event.target.value.trim(),
    }, name);
    setNoteValidation(prev => {
      const filteredFields = prev.fields.filter(f => f.field !== name);
      const mergedFields = [...filteredFields, ...newValidation.fields];

      return {
        validate: mergedFields.length === 0,
        fields: mergedFields,
      };
    });
  }, [noteFormData]);

  const handleNoteSubmit = useCallback(async (event?: React.FormEvent) => {
    event && event.preventDefault();

    const newValidation = validateNoteForm(noteFormData);
    setNoteValidation(newValidation);
    if (!newValidation.validate) return;

    setNoteLoading(true);

    try {
      const resolvedAction = noteFormData.action === '__blank__' ? '' : noteFormData.action;

      if (noteModal.item) {
        const payload: Record<string, any> = {
          notes: noteFormData.notes?.trim() || '',
        };
        if (canVerify) {
          payload.verification = resolvedAction;
        }
        if (noteModal.item.payout_id) {
          await PayoutService.saveNote(noteModal.item.payout_id, payload);
        } else {
          await PayoutService.addPayout(noteModal.item.reservation_id, payload);
        }
      } else {
        const payload: Record<string, any> = {
          notes: noteFormData.notes?.trim() || '',
          ids: selectedIds,
          override: noteFormData.override,
        };
        if (canVerify) {
          payload.verification = resolvedAction;
        }
        await PayoutService.saveNote('bulk', payload);
      }

      toast.success(<Toast message="Data saved successfully." />, { icon: false });

      setNoteLoading(false);
      handleNoteClose();
      setCounter(prev => prev + 1);
    } catch (error) {
      setNoteLoading(false);
    }
  }, [noteFormData, noteModal.item, selectedIds, handleNoteClose]);

  const headerSection = <TableHeaderSection total={total} kpis={kpis} selectAll={selectAll} onSelectAll={handleSelectAll} onAddNote={() => handleNoteClick(null)} bulkNoteDisabled={selectedIds.length <= 1} />;

  const values: TableContextProvider = useMemo(() => {
    return {
      rowProps: {
        selectedIds,
        onSelect: handleSelect,
        onPayoutAmtClick: handlePayoutAmtClick,
        onNoteClick: handleNoteClick,
      },
      headerSection,
      row: Row,
      data: payouts || [],
      pagination: true,
      headCells,
      order,
      orderBy,
      page,
      perPage,
      total,
      loading,
      noDataFoundMessage: t('reconciliation.payout.list.pageTexts.table.noDataFoundMessage'),
      setPage,
      setPerPage,
      setOrder,
      setOrderBy,
    };
  }, [
    selectedIds, handleSelect, handlePayoutAmtClick, handleNoteClick,
    headerSection, payouts, headCells,
    order, orderBy, page, perPage, total, loading,
    setPage, setPerPage, setOrder, setOrderBy,
  ]);

  return (
    <>
      <Header onRefresh={() => setCounter(prev => prev + 1)} />

      <Filters
        properties={properties}
        allProperties={allProperties}
        managers={managers}
        allManagers={allManagers}
        platforms={platforms}
        allPlatforms={allPlatforms}
        bankAccounts={bankAccounts}
        allBankAccounts={allBankAccounts}
        payoutTypes={payoutTypes}
        allPayoutTypes={allPayoutTypes}
        payoutStatuses={payoutStatuses}
        allPayoutStatuses={allPayoutStatuses}
        reservationStatus={reservationStatus}
        allReservationStatus={allReservationStatus}
        actions={actions}
        allActions={allActions}
        payoutManagedBy={payoutManagedBy}
        allPayoutManagedBy={allPayoutManagedBy}
        dropdownSearchData={dropdownSearchData}
        onDropdownSearchChange={handleDropdownSearchInputChange}
        initialFilters={initialFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />

      <TableBox tableValues={values} />

      {/* ---- Payout Amount Modal ---- */}
      <Modal
        open={payoutAmtModal.open}
        onClose={handlePayoutAmtClose}
        maxWidth="md"
        titleClassName="text-start"
        actionClassName="justify-content-start"
        title={t('reconciliation.payout.list.pageTexts.modals.payoutAmt.title')}
        content={<PayoutAmtModalContent item={payoutAmtModal.item} />}
        action={
          <Button
            className="btn btn-outline-secondary"
            text={t('reconciliation.payout.list.pageTexts.modals.payoutAmt.buttons.close')}
            onClick={handlePayoutAmtClose}
          />
        }
      />

      {/* ---- Note Modal ---- */}
      <Modal
        open={noteModal.open}
        onClose={handleNoteClose}
        maxWidth="sm"
        titleClassName='text-start'
        actionClassName='justify-content-start'
        title={t('reconciliation.payout.list.pageTexts.modals.note.title')}
        content={
          <NoteModalContent
            item={noteModal.item}
            formData={noteFormData}
            validation={noteValidation}
            actions={actions}
            allActions={allActions}
            dropdownSearchData={dropdownSearchData}
            onDropdownSearchChange={handleDropdownSearchInputChange}
            onInputChange={handleNoteInputChange}
            onInputBlur={handleNoteInputBlur}
            canVerify={canVerify}
          />
        }
        action={
          <NoteModalButtons
            loading={noteLoading}
            onCancel={handleNoteClose}
            onSave={handleNoteSubmit}
          />
        }
      />
    </>
  );
};

export default List;
